import { create } from 'zustand';
import type { Admin, AdminRole, AdminSession } from '../api/types';

const KEY = 'tabe3-crm-session';

interface Stored {
  accessToken: string;
  refreshToken: string;
  admin: Admin;
}

interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  admin: Admin | null;
  /** Why the last session ended, for the sign-in screen to say. */
  endedBecause: string | null;
  signIn: (session: AdminSession) => void;
  updateAdmin: (admin: Admin) => void;
  requirePasswordChange: () => void;
  signOut: (reason?: string) => void;
}

/**
 * Kept in sessionStorage, not localStorage: closing the browser ends an admin session, and a
 * shared office computer does not stay signed in to the platform overnight.
 */
function load(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

function save(stored: Stored | null): void {
  try {
    if (stored) {
      sessionStorage.setItem(KEY, JSON.stringify(stored));
    } else {
      sessionStorage.removeItem(KEY);
    }
  } catch {
    // Private mode or storage blocked: the session simply does not survive a reload.
  }
}

const initial = load();

export const useSession = create<SessionState>((set, get) => ({
  accessToken: initial?.accessToken ?? null,
  refreshToken: initial?.refreshToken ?? null,
  admin: initial?.admin ?? null,
  endedBecause: null,
  signIn: (session) => {
    save({ accessToken: session.accessToken, refreshToken: session.refreshToken, admin: session.admin });
    set({ accessToken: session.accessToken, refreshToken: session.refreshToken, admin: session.admin, endedBecause: null });
  },
  updateAdmin: (admin) => {
    const { accessToken, refreshToken } = get();
    if (accessToken && refreshToken) {
      save({ accessToken, refreshToken, admin });
    }
    set({ admin });
  },
  requirePasswordChange: () => {
    const admin = get().admin;
    if (admin && !admin.mustChangePassword) {
      get().updateAdmin({ ...admin, mustChangePassword: true });
    }
  },
  signOut: (reason) => {
    save(null);
    set({ accessToken: null, refreshToken: null, admin: null, endedBecause: reason ?? null });
  },
}));

/**
 * What each role may change, mirrored from the server's {@code AdminAccess} only to hide buttons.
 * The server decides; a missing check here is cosmetic.
 */
const CAN = {
  manageStores: ['SUPER_ADMIN', 'SUPPORT'],
  billing: ['SUPER_ADMIN', 'FINANCE'],
  notes: ['SUPER_ADMIN', 'SUPPORT', 'FINANCE'],
  team: ['SUPER_ADMIN'],
} satisfies Record<string, AdminRole[]>;

export type Capability = keyof typeof CAN;

export function can(admin: Pick<Admin, 'role'> | null | undefined, capability: Capability): boolean {
  return !!admin && (CAN[capability] as readonly AdminRole[]).includes(admin.role);
}

export function useCan(capability: Capability): boolean {
  return can(useSession((state) => state.admin), capability);
}
